import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { getInitials, getRoleLabel } from "../lib/utils";
import { User, Mail, Phone, Shield, Bell, Lock, Database, Trash2, AlertTriangle, Loader2, Eye, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useHiddenCards, DASHBOARD_CARDS, CLIENTWISE_CARDS } from "../lib/cardPreferences";
import ReportingDeliveryCard from "../components/ReportingDeliveryCard";
import PortfolioCategoriesCard from "../components/PortfolioCategoriesCard";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user, isSuperAdmin, logout } = useAuth();
  const isAssociate = user?.role === "sub_broker";
  const canSeePreferences = isSuperAdmin || isAssociate;
  const [clearing, setClearing] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const navigate = useNavigate();

  const handleSignOut = () => {
    if (!window.confirm("Sign out of Kinntegra?")) return;
    logout();
    navigate("/about", { replace: true });
  };

  const handleSave = () => {
    toast.success("Settings saved successfully");
  };

  const handleClearDatabase = async () => {
    if (confirmText !== "DELETE ALL DATA") {
      toast.error("Please type 'DELETE ALL DATA' to confirm");
      return;
    }

    setClearing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API_URL}/data/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const remaining = response.data.remaining_total || 0;
        toast.success("Database Cleared!", {
          description: `Deleted ${response.data.total_deleted} documents. ${remaining} remain (user accounts).`,
        });
        setConfirmText("");
      }
    } catch (error) {
      console.error("Clear database error:", error);
      toast.error("Failed to clear database", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
        <p className="text-gray-500">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <Avatar className="w-24 h-24 mx-auto mb-4">
                <AvatarFallback
                  className="text-2xl text-white font-semibold"
                  style={{ backgroundColor: "#2d6b62" }}
                >
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <h3 className="text-xl font-semibold text-gray-800">{user?.name}</h3>
              <p className="text-gray-500 capitalize">{getRoleLabel(user?.role)}</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span>{user?.email}</span>
                </div>
                {user?.phone && (
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{user?.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    defaultValue={user?.name}
                    data-testid="settings-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    defaultValue={user?.email}
                    disabled
                    data-testid="settings-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    defaultValue={user?.phone}
                    data-testid="settings-phone-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleSave}
                style={{ backgroundColor: "#2d6b62" }}
                data-testid="save-profile-button"
              >
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="w-5 h-5" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                </div>
                <div></div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button variant="outline" data-testid="change-password-button">
                Change Password
              </Button>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="w-5 h-5" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive transaction updates via email</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">SMS Alerts</p>
                    <p className="text-sm text-gray-500">Get SMS for important transactions</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 rounded" />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">Marketing Communications</p>
                    <p className="text-sm text-gray-500">Receive promotional updates and offers</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 rounded" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Display Preferences — show/hide KPI cards per page */}
          <DisplayPreferencesCard />

          {/* Preferences — auto-email sender details. Visible to super-admin (firm-wide
              fallback) and each associate (their own scope). */}
          {canSeePreferences && (
            <Card data-testid="settings-preferences-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="w-5 h-5" />
                  Preferences
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Configure the sender identity used by the auto-email engine for the clients you manage.
                  Schedule, report toggles, per-client opt-outs and the Test send button live under
                  Portfolio → Reporting Preferences.
                </p>
              </CardHeader>
              <CardContent>
                <ReportingDeliveryCard />
              </CardContent>
            </Card>
          )}

          {/* Database Management — visible only to super-admins (broker themselves). Hidden from associates + clients. */}
          {isSuperAdmin && (
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                  <Database className="w-5 h-5" />
                  Database Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-red-100/50 rounded-lg border border-red-200">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Danger Zone</p>
                      <p className="text-sm text-red-600 mt-1">
                        Clearing the database will permanently delete all data including:
                      </p>
                      <ul className="text-sm text-red-600 mt-2 list-disc list-inside space-y-1">
                        <li>All client holdings and investor data</li>
                        <li>All transactions (Karvy and CAMS)</li>
                        <li>All scheme master data</li>
                        <li>All reconciliation data and logs</li>
                        <li>All feed logs and report statuses</li>
                      </ul>
                      <p className="text-sm text-red-700 font-medium mt-2">
                        User accounts will be preserved.
                      </p>
                    </div>
                  </div>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive" 
                        className="w-full"
                        data-testid="clear-database-button"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Entire Database
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-5 h-5" />
                          Clear All Database Data?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                          <p>
                            This action <strong>cannot be undone</strong>. This will permanently delete
                            all data from the database.
                          </p>
                          <div className="mt-4">
                            <Label htmlFor="confirm-delete" className="text-sm font-medium">
                              Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE ALL DATA</span> to confirm:
                            </Label>
                            <Input
                              id="confirm-delete"
                              value={confirmText}
                              onChange={(e) => setConfirmText(e.target.value)}
                              placeholder="DELETE ALL DATA"
                              className="mt-2"
                              data-testid="confirm-delete-input"
                            />
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setConfirmText("")}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleClearDatabase}
                          disabled={confirmText !== "DELETE ALL DATA" || clearing}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="confirm-clear-button"
                        >
                          {clearing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Clearing...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Clear Database
                            </>
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sign Out — visible to every role so users always have a clear way to log out */}
          <Card data-testid="settings-signout-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <LogOut className="w-5 h-5" />
                Sign Out
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Signed in as{" "}
                <span className="font-medium text-gray-900">
                  {user?.name || user?.email}
                </span>
                . Click below to end your session and return to the public site.
              </p>
              <Button
                variant="outline"
                onClick={handleSignOut}
                data-testid="settings-signout-button"
                className="w-full sm:w-auto"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;

// ── Display Preferences Card ────────────────────────────────────────────
// Standalone sub-component so each page's state stays isolated (different
// `useHiddenCards("...")` instances) without forcing a parent re-render.
const DisplayPreferencesCard = () => {
  const dash = useHiddenCards("dashboard");
  const cwise = useHiddenCards("clientwise");
  const renderGroup = (title, pageKey, defs, h) => (
    <div>
      <p className="font-medium text-gray-800 mb-2">{title}</p>
      <p className="text-xs text-gray-500 mb-3">Untick a card to hide it on the {title.toLowerCase()} page.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {defs.map((c) => {
          const isVisible = !h.hidden[c.key];
          return (
            <label
              key={`${pageKey}-${c.key}`}
              className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
              data-testid={`pref-${pageKey}-${c.key}`}
            >
              <span className={`${isVisible ? "text-gray-800" : "text-gray-400 line-through"}`}>{c.label}</span>
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => h.toggle(c.key, !e.target.checked)}
                className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500"
              />
            </label>
          );
        })}
      </div>
    </div>
  );
  return (
    <Card data-testid="display-preferences-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Eye className="w-5 h-5" />
          Display Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderGroup("Dashboard", "dashboard", DASHBOARD_CARDS, dash)}
        <Separator />
        {renderGroup("Client Wise Summary", "clientwise", CLIENTWISE_CARDS, cwise)}
      </CardContent>
    </Card>
  );
};
